import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ShieldCheck, ChevronLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useConsent, CATEGORIES } from "@/context/ConsentContext";
import { SmartLink } from "@/components/SmartLink";

const Toggle = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
      checked ? "bg-[var(--olive)]" : "bg-white/15"
    } ${disabled ? "cursor-not-allowed opacity-70" : ""}`}
  >
    <span
      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-[22px]" : "translate-x-0.5"
      }`}
    />
  </button>
);

export const CookieBanner = () => {
  const { t } = useLanguage();
  const c = t.consent;
  const consent = useConsent();
  const [draft, setDraft] = useState(consent.choices);

  useEffect(() => {
    if (consent.open) setDraft(consent.choices);
  }, [consent.open, consent.choices]);

  return (
    <AnimatePresence>
      {consent.open && (
        <motion.div
          key="consent"
          role="dialog"
          aria-modal="false"
          aria-labelledby="consent-title"
          data-testid="cookie-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
          className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-xl sm:inset-x-auto sm:bottom-5 sm:left-5 sm:mx-0"
        >
          <div className="glass max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-3xl border border-[var(--line-strong)] p-5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] sm:p-6">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[var(--line)] bg-[var(--surface)]">
                {consent.settingsView ? <ShieldCheck className="h-5 w-5 text-[var(--sage)]" /> : <Cookie className="h-5 w-5 text-[var(--sage)]" />}
              </span>
              <div className="min-w-0 flex-1">
                <h2 id="consent-title" className="font-display text-xl text-[var(--ink)]">
                  {c.title}
                </h2>
                {!consent.settingsView && <p className="mt-2 text-sm leading-relaxed text-[var(--ink-2)]">{c.body}</p>}
              </div>
            </div>

            <AnimatePresence initial={false} mode="wait">
              {consent.settingsView ? (
                <motion.div key="settings" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                  <ul className="mt-5 divide-y divide-[var(--line)] border-y border-[var(--line)]">
                    {c.categories.map((cat) => (
                      <li key={cat.id} className="flex items-start justify-between gap-4 py-4">
                        <div>
                          <p className="text-sm font-semibold text-[var(--ink)]">
                            {cat.name}
                            {cat.required && <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">{c.alwaysOn}</span>}
                          </p>
                          <p className="mt-1 text-xs leading-relaxed text-[var(--ink-2)]">{cat.desc}</p>
                        </div>
                        <Toggle
                          label={cat.name}
                          checked={cat.required ? true : Boolean(draft[cat.id])}
                          disabled={cat.required}
                          onChange={(v) => setDraft((d) => ({ ...d, [cat.id]: v }))}
                        />
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <button type="button" onClick={() => consent.decide(draft)} className="btn btn-primary px-5 py-2.5 text-sm" data-testid="consent-save">
                      {c.save}
                    </button>
                    <button type="button" onClick={consent.acceptAll} className="btn btn-ghost px-5 py-2.5 text-sm" data-testid="consent-accept-all-settings">
                      {c.acceptAll}
                    </button>
                    <button
                      type="button"
                      onClick={() => consent.setSettingsView(false)}
                      className="inline-flex items-center gap-1 px-2 py-2 text-sm text-[var(--ink-2)] hover:text-white sm:ml-auto"
                      data-testid="consent-back"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {c.back}
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="main" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                  <div className="mt-5 grid gap-2 sm:grid-cols-2">
                    <button type="button" onClick={consent.acceptAll} className="btn btn-primary px-5 py-2.5 text-sm" data-testid="consent-accept-all">
                      {c.acceptAll}
                    </button>
                    <button type="button" onClick={consent.rejectAll} className="btn btn-ghost px-5 py-2.5 text-sm" data-testid="consent-reject-all">
                      {c.rejectAll}
                    </button>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--ink-3)]">
                    <button type="button" onClick={() => consent.setSettingsView(true)} className="underline decoration-[var(--line-strong)] underline-offset-2 hover:text-white" data-testid="consent-settings">
                      {c.settings}
                    </button>
                    <SmartLink href="/datenschutz" className="hover:text-white">
                      {c.privacy}
                    </SmartLink>
                    <SmartLink href="/impressum" className="hover:text-white">
                      {c.imprint}
                    </SmartLink>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
